import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFieldArray } from "react-hook-form";

import {
    Card,
    CardContent
} from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Info } from "lucide-react";
import { OptionsFieldArrayProps } from ".";

export function OptionsFieldArray({
    index,
    parameterName,
    form
}: OptionsFieldArrayProps) {
    const { fields: optionFields, append, remove } = useFieldArray({
        control: form.control,
        name: `parameter.${parameterName}.${index}.options`,
    });

    const appendParameter = () => {
        append({ key: "", value: "" });
    }

    const deleteParameter = (index: number) => {
        remove(index);
    };

    return (
        <Card className="rounded-lg">
            <CardContent className="px-4 py-3 space-y-2">
                {optionFields.map((option, optionIndex) => (
                    <div key={option.id} className="flex items-center gap-2">
                        <FormField
                            control={form.control}
                            name={`parameter.${parameterName}.${index}.options.${optionIndex}.key`}
                            render={({ field }) => (
                                <FormItem className="flex-1">
                                    <FormLabel className="relative">
                                        Name (optional)
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild className="absolute -translate-y-1/2 top-1/2 -right-5">
                                                    <Info size={14} className="text-blue-600" />
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-[400px]">
                                                    <p>
                                                        The name will be displayed in the element selection field and is optional.
                                                        It is intended to help identify the entered value.
                                                        If the field is left empty, the value from the "value" field will be used as the name
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </FormLabel>
                                    <Input placeholder="Option Name" {...field} />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name={`parameter.${parameterName}.${index}.options.${optionIndex}.value`}
                            render={({ field }) => (
                                <FormItem className="flex-1">
                                    <FormLabel className="">Value</FormLabel>
                                    <div className="flex items-center gap-2">
                                        <Input placeholder="Option Value" {...field} />
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="destructive"
                                                        size='action'
                                                        className={`text-nowrap flex text-sm`}
                                                        onClick={() => deleteParameter(index)}
                                                    >
                                                        <i className="text-md bi bi-trash"></i>
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Delete</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                ))}

                <div className="flex justify-end w-full">
                    <Button
                        type="button"
                        size='sm'
                        className="text-xs"
                        onClick={(e) => {
                            e.preventDefault()
                            appendParameter()
                        }}
                    >
                        Add value option
                    </Button>
                </div>

            </CardContent>
        </Card>
    );
}


